import React, { createContext, useContext, useState, useCallback } from 'react';

const OwnerViewContext = createContext();

export const useOwnerView = () => {
  const ctx = useContext(OwnerViewContext);
  if (!ctx) throw new Error('useOwnerView must be used within OwnerViewProvider');
  return ctx;
};

/**
 * OwnerViewProvider — lets the system owner temporarily "view as" any agency
 * without swapping sessions or logging in as a different user.
 *
 * When viewMode is true:
 *  - effectiveUser is a synthetic user object shaped like an agency admin
 *  - All pages that call useEffectiveUser() will see the agency context
 *  - The owner's real session / JWT is untouched
 */
export const OwnerViewProvider = ({ children }) => {
  const [viewMode, setViewMode] = useState(false);
  const [viewAgency, setViewAgency] = useState(null); // full agency object from owner dashboard

  const enterViewMode = useCallback((agency) => {
    setViewAgency(agency);
    setViewMode(true);
  }, []);

  const exitViewMode = useCallback(() => {
    setViewMode(false);
    setViewAgency(null);
  }, []);

  // Build a synthetic "admin user" for the selected agency so pages render correctly
  const effectiveUser = viewMode && viewAgency
    ? {
        _id: 'owner-view-synthetic',
        firstName: 'Owner',
        lastName: 'Preview',
        fullName: 'Owner Preview',
        badgeNumber: 'OWNER',
        email: 'owner@preview',
        rank: 'admin',
        role: 'admin',
        department: viewAgency.name,
        agency: {
          _id: viewAgency._id,
          name: viewAgency.name,
          code: viewAgency.code,
          storage: viewAgency.storage || { used: 0, limit: 536870912000 },
          billing: viewAgency.billing || { planType: 'standard', status: 'trialing' },
          mfaRequired: viewAgency.mfaRequired || false
        },
        permissions: {
          canCreateCases: true,
          canEditCases: true,
          canDeleteCases: true,
          canManageEvidence: true,
          canManageDocuments: true,
          canManagePersonnel: true,
          canDownloadCases: true,
          canViewConfidential: true,
          canViewAnalytics: true
        },
        isActive: true,
        isAgencyOwner: true,
        ownerViewMode: true  // flag so pages can detect this is a preview
      }
    : null;

  return (
    <OwnerViewContext.Provider value={{ viewMode, viewAgency, effectiveUser, enterViewMode, exitViewMode }}>
      {children}
    </OwnerViewContext.Provider>
  );
};
